// frontend/pages/Expenses.jsx
import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { PlusIcon, PencilIcon, TrashIcon } from '@heroicons/react/24/outline';
import { expenseService } from '../services/api';
import DataTable from '../components/Common/DataTable';
import Modal from '../components/Common/Modal';
import ExpenseForm from '../components/Expenses/ExpenseForm';
import { toast } from 'react-hot-toast';

export default function Expenses() {
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingExpense, setEditingExpense] = useState(null);
  const [dateRange, setDateRange] = useState({
    startDate: new Date(new Date().setDate(1)).toISOString().split('T')[0],
    endDate: new Date().toISOString().split('T')[0]
  });

  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ['expenses', dateRange],
    queryFn: () => expenseService.getExpenses(dateRange)
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => expenseService.deleteExpense(id),
    onSuccess: () => {
      toast.success('Expense deleted successfully');
      queryClient.invalidateQueries(['expenses']);
    },
    onError: (error) => {
      toast.error(error.response?.data?.error || 'Failed to delete expense');
    }
  });

  const columns = [
    { key: 'expense_date', header: 'Date', sortable: true, format: 'date' },
    { key: 'category', header: 'Category', sortable: true },
    { key: 'description', header: 'Description' },
    { key: 'vendor_name', header: 'Vendor' },
    { key: 'amount', header: 'Amount', sortable: true, format: 'currency', className: 'font-semibold text-red-600' },
    { key: 'tax_amount', header: 'Tax', format: 'currency' },
    { key: 'created_by_name', header: 'Recorded By' }
  ];

  const handleDelete = (expense) => {
    const amount = typeof expense.amount === 'number' ? expense.amount : parseFloat(expense.amount) || 0;
    if (window.confirm(`Delete expense of KES ${amount.toLocaleString()}?`)) {
      deleteMutation.mutate(expense.id);
    }
  };

  const actions = [
    {
      label: 'Edit',
      onClick: (row) => setEditingExpense(row),
      icon: 'edit'
    },
    {
      label: 'Delete',
      onClick: handleDelete,
      icon: 'delete',
      className: 'text-red-600'
    }
  ];

  // ✅ FIX: Proper number parsing to avoid string concatenation
  const expensesData = data?.data || [];
  
  const totalExpenses = expensesData.reduce((sum, e) => {
    const amount = typeof e.amount === 'number' ? e.amount : parseFloat(e.amount) || 0;
    return sum + amount;
  }, 0);
  
  const totalTax = expensesData.reduce((sum, e) => {
    const tax = typeof e.tax_amount === 'number' ? e.tax_amount : parseFloat(e.tax_amount) || 0;
    return sum + tax;
  }, 0);
  
  const categorySummary = data?.summary?.categoryBreakdown || [];

  // ✅ Calculate average daily expense based on actual date range
  const startDate = new Date(dateRange.startDate);
  const endDate = new Date(dateRange.endDate);
  const daysDiff = Math.max(1, Math.ceil((endDate - startDate) / (1000 * 60 * 60 * 24)));
  const avgDailyExpense = totalExpenses / daysDiff;

  // ✅ Helper function to format currency
  const formatCurrency = (value) => {
    const num = typeof value === 'number' ? value : parseFloat(value) || 0;
    return num.toLocaleString('en-KE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  // ✅ Get top category safely
  const topCategory = categorySummary[0];
  const topCategoryName = topCategory?.category?.replace(/_/g, ' ') || 'N/A';
  const topCategoryAmount = topCategory?.total_amount ? (typeof topCategory.total_amount === 'number' ? topCategory.total_amount : parseFloat(topCategory.total_amount) || 0) : 0;

  return (
    <div>
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Expenses</h1>
          <p className="text-gray-600">Track and manage business expenses</p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          <PlusIcon className="h-5 w-5 mr-2" />
          Add Expense
        </button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-white p-4 rounded-lg shadow">
          <div className="text-sm text-gray-500">Total Expenses</div>
          <div className="text-2xl font-bold text-red-600">
            KES {formatCurrency(totalExpenses)}
          </div>
        </div>
        <div className="bg-white p-4 rounded-lg shadow">
          <div className="text-sm text-gray-500">Total Tax (Input VAT)</div>
          <div className="text-2xl font-bold text-orange-600">
            KES {formatCurrency(totalTax)}
          </div>
        </div>
        <div className="bg-white p-4 rounded-lg shadow">
          <div className="text-sm text-gray-500">Average Daily Expense</div>
          <div className="text-2xl font-bold">
            KES {formatCurrency(avgDailyExpense)}
          </div>
        </div>
        <div className="bg-white p-4 rounded-lg shadow">
          <div className="text-sm text-gray-500">Top Category</div>
          <div className="text-lg font-semibold truncate capitalize">
            {topCategoryName}
          </div>
          <div className="text-sm text-gray-600">
            KES {formatCurrency(topCategoryAmount)}
          </div>
        </div>
      </div>

      {/* Date Range Filter */}
      <div className="bg-white p-4 rounded-lg shadow mb-6">
        <div className="flex space-x-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
            <input
              type="date"
              value={dateRange.startDate}
              onChange={(e) => setDateRange({ ...dateRange, startDate: e.target.value })}
              className="px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
            <input
              type="date"
              value={dateRange.endDate}
              onChange={(e) => setDateRange({ ...dateRange, endDate: e.target.value })}
              className="px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>
      </div>

      {/* Category Breakdown */}
      <div className="bg-white p-4 rounded-lg shadow mb-6">
        <h3 className="text-lg font-semibold mb-3">Expense by Category</h3>
        <div className="space-y-2">
          {categorySummary.length === 0 ? (
            <p className="text-gray-500 text-center py-4">No expense data available</p>
          ) : (
            categorySummary.map(cat => {
              const categoryTotal = typeof cat.total_amount === 'number' ? cat.total_amount : parseFloat(cat.total_amount) || 0;
              const percentage = totalExpenses > 0 ? (categoryTotal / totalExpenses) * 100 : 0;
              return (
                <div key={cat.category}>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="capitalize">{cat.category.replace(/_/g, ' ')}</span>
                    <span>KES {formatCurrency(categoryTotal)} ({cat.count} transactions)</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className="bg-blue-600 rounded-full h-2 transition-all duration-500"
                      style={{ width: `${Math.min(percentage, 100)}%` }}
                    />
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* Data Table */}
      <DataTable
        columns={columns}
        data={expensesData}
        loading={isLoading}
        actions={actions}
        pagination={data?.pagination}
      />

      {/* Create/Edit Expense Modal */}
      <Modal
        isOpen={showCreateModal || !!editingExpense}
        onClose={() => {
          setShowCreateModal(false);
          setEditingExpense(null);
        }}
        title={editingExpense ? 'Edit Expense' : 'Add Expense'}
      >
        <ExpenseForm
          expense={editingExpense}
          onSuccess={() => {
            setShowCreateModal(false);
            setEditingExpense(null);
            queryClient.invalidateQueries(['expenses']);
          }}
          onCancel={() => {
            setShowCreateModal(false);
            setEditingExpense(null);
          }}
        />
      </Modal>
    </div>
  );
}